import { Component, computed, inject, OnInit } from '@angular/core';
import { of } from 'rxjs';
import { catchError, take } from 'rxjs/operators';
import { ActivatedRoute } from '@angular/router';
import { EventconfigStoreService } from '../../../shared/eventconfig-store.service';
import { EventsetupStoreService } from '../../../shared/eventsetup-store.service';
import { EventAuthService } from '../../../shared/event-auth.service';
import { AuthMode } from '../../../shared/auth-mode.enum';
import { AuthService } from '../../../core/_services/auth.service';
import { TokenStorageService } from '../../../core/_services/token-storage.service';
import { UserAssignService } from '../../../shared/user-assign.service';
import { MediaLibraryStoreService } from '../../../shared/media-library-store.service';
import { Router } from '@angular/router';

@Component({
    selector: 'lse-login',
    templateUrl: './login.component.html',
    styleUrls: ['./login.component.css'],
    standalone: false
})
export class LoginComponent implements OnInit {

  // Enum für Template-Zugriff
  AuthMode = AuthMode;

  form: any = {};
  isLoggedIn = false;
  isLoginFailed = false;
  errorMessage = '';
  email = '';
  user: any;

  eventToken = '';
  authMode: AuthMode | null = null;
  loading = true;

  // Interner Nutzer?
  uInternal = false;
  uMode: 'int' | 'ext' = 'ext';

  // EMAIL_TOKEN: Bestätigungs-Mail gesendet
  emailSent = false;
  /**
   * Zustellart aus der Event-Konfiguration.
   *
   * 'code' zeigt nach dem Versand ein Eingabefeld statt des Hinweises "Mail unterwegs" —
   * der Zuschauer tippt sechs Ziffern ein, statt einem Link zu folgen.
   */
  deliveryMode: 'link' | 'code' = 'link';
  /** Vom Zuschauer eingetippter Code */
  confirmationCode = '';

  /**
   * Wurde beim Tippen etwas verworfen, das keine Ziffer ist?
   *
   * Der Code besteht aus sechs Ziffern. Buchstaben werden aus dem Feld entfernt, statt
   * sie stehen zu lassen — sonst wäre der Knopf bei sechs Buchstaben freigegeben, das
   * Absenden liefe aber ins Leere. Damit das Verschwinden nicht wie ein Fehler des
   * Feldes wirkt, wird es erklärt.
   */
  codeHadInvalidChars = false;
  codeSubmitting = false;
  // EMAIL_TOKEN: Token aus Bestätigungs-Link (Query-Param)
  confirmationToken = '';

  // MEDIA_LIBRARY: Token aus Query-Param
  mToken = '';
  mBackLinkDecoded = '';

  /**
   * Regulärer Auth-Modus des Events, angeboten als Ausweg nach einem gescheiterten
   * Mediathek-Login. Null heißt: kein Ausweg anzubieten.
   */
  fallbackAuthMode: AuthMode | null = null;

  /** Eventsetup, gemerkt für die Auflösung des Auth-Modus nach dem ersten Durchlauf. */
  private setup: any = null;

  eventTitle = '';
  eventSubtitle = '';

  private es = inject(EventconfigStoreService);
  readonly activeLang = this.es.activeLang;

  constructor(
    private authService: AuthService,
    private tokenStorage: TokenStorageService,
    private activatedRoute: ActivatedRoute,
    private ep: EventsetupStoreService,
    private eventAuthService: EventAuthService,
    private us: UserAssignService,
    private mediaLib: MediaLibraryStoreService,
    private router: Router
  ) {}

  ngOnInit(): void {
    // Event-Token aus der Route lesen
    const params = this.activatedRoute.snapshot.paramMap;
    this.eventToken = params.get('token') || '';

    if (!this.eventToken) {
      this.loading = false;
      return;
    }

    // Token in Services setzen
    this.es.setToken(this.eventToken);
    this.tokenStorage.setEventToken(this.eventToken);

    // Query-Parameter auslesen (ln, medialib, confirm-token, back)
    this.activatedRoute.queryParams.pipe(take(1)).subscribe(qp => {
      if (qp.ln) {
        this.es.setActiveLang(qp.ln);
      }
      if (qp.medialib) {
        this.mToken = encodeURIComponent(qp.medialib);
      }
      if (qp.back) {
        this.mBackLinkDecoded = decodeURI(atob(qp.back));
      }
      if (qp.confirmToken) {
        this.confirmationToken = qp.confirmToken;
      }
    });

    // Event initialisieren
    this.es.initEvent(this.eventToken).subscribe(p => {
      if (p === null || p === undefined) {
        this.loading = false;
        this.errorMessage = 'Event nicht gefunden.';
        return;
      }
      this.es.setInitCipher(p.token);
    });

  


    this.ep.setup$.pipe(take(1)).subscribe(setup => {
      if (!setup) {
        this.loading = false;
        this.errorMessage = 'Event-Setup konnte nicht geladen werden.';
        return;
      }
      this.setup = setup;

      // Sprache erst hier festlegen: welche Sprachen das Event hat, steht im Setup.
      // Ohne gültiges ?ln= gilt die erste Event-Sprache — sonst bliebe der Store auf
      // seinem Default 'de', auch wenn das Event nur Englisch kennt.
      const eventLangs: string[] = setup.lang ?? [];
      if (eventLangs.length && !eventLangs.includes(this.activeLang())) {
        this.es.setActiveLang(eventLangs[0]);
      }
      this.es.getConfig(this.activeLang()).pipe(take(1)).subscribe(config => {
        this.eventTitle = config.title;
        this.eventSubtitle = config.subtitle;
      });

      // Der Mediathek-Token ist ein Credential, das der Aufruf mitbringt — kein Modus,
      // den das Event konfiguriert. Er gilt deshalb vorrangig: wer aus der Mediathek
      // kommt, ist dort bereits angemeldet und darf nicht auf dem Login-Formular landen,
      // nur weil für das Event 'email_password' hinterlegt ist.
      if (this.mToken) {
        // Der Netzwerk-Test läuft mit, hält den Login aber nicht auf: sein Ergebnis
        // braucht erst der Player für die Stream-Auswahl, nicht die Anmeldung.
        if (this.us.hasDistributionNetwork()) {
          this.checkInternalUser(() => { /* nur Seiteneffekt: uMode + UserAssignService */ });
        }
        this.handleAuthMode(AuthMode.MEDIA_LIBRARY);
        return;
      }

      this.resolveAuthMode(mode => this.handleAuthMode(mode));
    });
  }

  // ── Auth-Modus ermitteln ───────────────────────────────────────

  /**
   * Ermittelt den Auth-Modus für diesen Nutzer und übergibt ihn dem Callback.
   *
   * Zwei Aufrufer: der Seitenaufbau (der den Modus direkt umsetzt) und der gescheiterte
   * Mediathek-Login (der ihn nur als Ausweg anbietet).
   *
   * Maßgeblich ist das Event-Dokument; fehlt es, gilt config_setup.json. Weil der Modus
   * pro Netzwerk-Scope gelten kann, muss der Pixel-Test vorher durch sein.
   */
  private resolveAuthMode(done: (mode: AuthMode) => void): void {
    // Die Konfiguration wird zuerst geholt: sie sagt unter anderem, ob der Kunde
    // überhaupt ein internes Verteilnetz hat. Ohne eines kann der Pixel-Test nur
    // 'ext' ergeben, und wir sparen uns bis zu zwei Sekunden Wartezeit.
    this.authService.getAuthConfig().pipe(
      take(1),
      catchError(() => of(null))
    ).subscribe(config => {
      // Bestimmt, ob nach dem Versand ein Code-Eingabefeld oder der Link-Hinweis kommt.
      this.deliveryMode = config?.deliveryMode === 'code' ? 'code' : 'link';

      const resolve = () => {
        const fromDocument = config?.authMode?.[this.uMode];

        // Kein Dokument (404) → Event ist noch nicht migriert, dann gilt weiterhin
        // config_setup.json.
        const mode = fromDocument
          ? this.eventAuthService.toAuthMode(fromDocument)
          : this.eventAuthService.resolveAuthModeFor(this.setup, this.uMode);

        done(mode);
      };

      // Zwei unabhängige Gründe, den Test zu überspringen: der Kunde hat laut
      // core.companies kein Verteilnetz, oder es stehen gar keine Server im Build.
      // Beide bedeuten, dass der Test nur 'ext' ergeben kann.
      //
      // Bei fehlender Konfiguration wird der Test gefahren: langsamer, aber sicher.
      // Ihn zu überspringen hieße, interne Nutzer als extern zu behandeln.
      if (config?.hasDistributionNetwork === false || !this.us.hasDistributionNetwork()) {
        this.uMode = 'ext';
        this.eventAuthService.setUserMode('ext');
        resolve();
        return;
      }

      // Der Pixel-Test muss abgeschlossen sein, bevor der Auth-Modus feststeht:
      // er bestimmt, welcher Scope (int/ext) gilt, und davon hängt ab, ob dieser
      // Nutzer überhaupt einen Login braucht.
      this.checkInternalUser(resolve);
    });
  }

  // ── Interner Nutzer prüfen ─────────────────────────────────────

  /**
   * Prüft via Pixel-Test ob der Nutzer intern ist.
   * Callback wird aufgerufen sobald das Ergebnis feststeht.
   */
  private checkInternalUser(callback: () => void): void {
    this.us.isInternalUser();
    let elapsed = 0;
    const interval = setInterval(() => {
      this.uInternal = this.us.getInternal();
      elapsed += 500;
      if (elapsed >= 2000 || this.uInternal || this.us.getComplete()) {
        this.uMode = this.uInternal ? 'int' : 'ext';
        this.eventAuthService.setUserMode(this.uMode);
        clearInterval(interval);
        callback();
      }
    }, 500);
  }

  // ── Setup verarbeiten / Auth-Modus setzen ──────────────────────

  /**
   * Reagiert auf den bereits aufgelösten Auth-Modus.
   *
   * Ob ein Scope (int/ext) überhaupt einen Login verlangt, ist an dieser Stelle schon
   * entschieden — ein für den Scope abgeschalteter Mechanismus kommt als NONE an.
   *
   *  NONE           → direkt weiter (Auto-Auth über initAuth)
   *  EMAIL_TOKEN    → Token aus Query vorhanden? Bestätigen. Sonst Formular zeigen.
   *  EMAIL_PASSWORD → Formular zeigen.
   *  MEDIA_LIBRARY  → mToken aus Query nutzen, automatisch authentifizieren.
   */
  private handleAuthMode(mode: AuthMode): void {
    this.authMode = mode;
    this.eventAuthService.setAuthMode(this.authMode);
    console.log('Auth-Modus:', this.authMode, '(Scope:', this.uMode + ')');

    switch (this.authMode) {

      // ─── NONE: kein Login ──────────────────────────────────────
      case AuthMode.NONE:
        this.autoAuth();
        break;

      // ─── EMAIL_TOKEN: Bestätigungs-Mail-Flow ──────────────────
      case AuthMode.EMAIL_TOKEN:
        if (this.confirmationToken) {
          // Nutzer kommt über Bestätigungs-Link → Token validieren
          this.confirmEmailToken(this.confirmationToken);
        } else {
          // Formular zeigen (nur E-Mail-Feld)
          this.loading = false;
        }
        break;

      // ─── EMAIL_PASSWORD: Benutzername + Passwort ──────────────
      case AuthMode.EMAIL_PASSWORD:
        this.loading = false;
        break;

      // ─── MEDIA_LIBRARY: Mediathek-Token ───────────────────────
      case AuthMode.MEDIA_LIBRARY:
        if (this.mToken) {
          this.mediaLibLogin();
        } else {
          // Kein Mediathek-Token → Fallback-Fehlermeldung
          this.loading = false;
          this.errorMessage = 'Kein Mediathek-Token vorhanden.';
        }
        break;

      default:
        this.loading = false;
        break;
    }
  }

  // ── Auth-Methoden ──────────────────────────────────────────────

  /**
   * Automatische Authentifizierung (Scope verlangt keinen Login).
   * Ruft initAuth auf – liefert der Server ein JWT, wird direkt weitergeleitet.
   */
  private autoAuth(): void {
    this.authService.initAuth(this.uMode).subscribe(
      data => {
        if (data && data.jwt) {
          this.tokenStorage.saveToken(data.jwt);
          this.tokenStorage.saveUser(data);
          this.isLoggedIn = true;
          this.trackAnonymousSession();
        } else {
          // Server hat kein JWT geliefert → Formular als Fallback
          this.loading = false;
        }
      },
      () => {
        this.loading = false;
        this.errorMessage = 'Automatische Anmeldung fehlgeschlagen.';
      }
    );
  }

  /**
   * Meldet einen Zugriff ohne Login, damit diese Nutzer in der CCP nicht komplett
   * unsichtbar bleiben. Gezählt werden Browser, nicht Personen — der Server weist sie
   * deshalb getrennt von den verifizierten Teilnehmern aus.
   *
   * Ob überhaupt gezählt wird, entscheidet der Server: nur bei Events, die mit
   * Magic-Link arbeiten.
   */
  private trackAnonymousSession(): void {
    const clientId = this.eventAuthService.getOrCreateClientId(this.eventToken);
    if (!clientId) return;

    this.authService.trackSession(clientId, this.uMode).subscribe({
      error: () => { /* Zählung darf den Seitenaufruf nie stören */ }
    });
  }

  /**
   * Mediathek-Login über mToken (MEDIA_LIBRARY-Modus).
   */
  private mediaLibLogin(): void {
    const backLinkToken = this.mBackLinkDecoded
      ? this.mBackLinkDecoded.substring(this.mBackLinkDecoded.lastIndexOf('/') + 1).split('?')[0]
      : this.eventToken;

    this.authService.mediaLibAuth(this.mToken, backLinkToken).subscribe(
      data => {
        if (data && data.jwt) {
          this.tokenStorage.saveToken(data.jwt);
          this.tokenStorage.saveUser(data);
          this.email = data.email;
          // Die Antwort bringt die übrigen Events der Mediathek mit — daraus baut der
          // Header sein Event-Menü.
          this.mediaLib.setEvents(data.tokens);
          this.isLoggedIn = true;
        } else {
          this.tokenStorage.signOut();
          this.handleMediaLibFailure('Mediathek-Anmeldung fehlgeschlagen.');
        }
      },
      err => {
        this.tokenStorage.signOut();
        this.handleMediaLibFailure(err.error?.message || 'Mediathek-Anmeldung fehlgeschlagen.');
      }
    );
  }

  /**
   * Der Mediathek-Token wurde nicht akzeptiert (abgelaufen, verfälscht, fremdes Event).
   *
   * Statt hier zu enden, wird der reguläre Auth-Modus des Events ermittelt: verlangt er
   * keinen Login, ist der Nutzer sofort drin, sonst bekommt er einen Button auf das
   * passende Formular. Der Weg zurück in die Mediathek steht daneben.
   */
  private handleMediaLibFailure(message: string): void {
    this.errorMessage = message;
    this.isLoginFailed = true;
    this.loading = false;

    this.resolveAuthMode(mode => {
      // Das Event kennt nur den Mediathek-Login — es gibt keine Alternative anzubieten.
      if (mode === AuthMode.MEDIA_LIBRARY) {
        return;
      }

      // Kein Login verlangt: dann braucht es keinen Button, das erledigt sich selbst.
      if (mode === AuthMode.NONE) {
        this.errorMessage = '';
        this.isLoginFailed = false;
        this.loading = true;
        this.handleAuthMode(mode);
        return;
      }

      this.fallbackAuthMode = mode;
    });
  }

  /**
   * Wechselt vom gescheiterten Mediathek-Login auf die reguläre Anmeldung des Events.
   */
  useRegularLogin(): void {
    if (!this.fallbackAuthMode) return;

    const mode = this.fallbackAuthMode;
    this.fallbackAuthMode = null;
    this.errorMessage = '';
    this.isLoginFailed = false;
    this.handleAuthMode(mode);
  }

  /** Rücksprung in die Mediathek, in der aktiven Sprache. */
  backToMediaLibrary(): string {
    return this.mBackLinkDecoded ? this.mBackLinkDecoded + '?ln=' + this.es.getActiveLang() : '';
  }

// In LoginComponent, füge diese Property hinzu:
private confirmInProgress = false;

// Und ändere confirmEmailToken:
private confirmEmailToken(token: string): void {
  // Verhindere Mehrfachaufrufe
  if (this.confirmInProgress) {
    return;
  }
  this.confirmInProgress = true;
  console.log('Bestätigungs-Token erhalten:', token);

  this.authService.confirmEmailToken(token).subscribe({
    next: (data) => {
      if (data && data.jwt) {
        console.log('Token bestätigt, JWT erhalten:', data.jwt);
        this.tokenStorage.saveToken(data.jwt);
        this.tokenStorage.saveUser(data);
        this.email = data.email;
        this.isLoggedIn = true;
        this.loading = false;

        // Query-Parameter entfernen, damit der Token nicht erneut gesendet wird
        this.router.navigate([], {
          relativeTo: this.activatedRoute,
          queryParams: { confirmToken: null },
          queryParamsHandling: 'merge',
          replaceUrl: true
        });
      } else {
        this.loading = false;
        this.errorMessage = 'Token ungültig oder abgelaufen.';
      }
      this.confirmInProgress = false;
    },
    error: () => {
      this.loading = false;
      this.errorMessage = 'Token ungültig oder abgelaufen.';
      this.confirmInProgress = false;

      // Auch bei Fehler den Query-Parameter entfernen, um Endlos-Loop zu verhindern
      this.router.navigate([], {
        relativeTo: this.activatedRoute,
        queryParams: { confirmToken: null },
        queryParamsHandling: 'merge',
        replaceUrl: true
      });
    }
  });
}
  // ── Formular-Submit ────────────────────────────────────────────

  /**
   * Löst den eingetippten Code ein.
   *
   * Der Code gilt genau einmal und stirbt nach fünf Fehlversuchen. Wie viele Versuche
   * bleiben, weiß nur der Server — dessen Meldung wird deshalb unverändert gezeigt.
   */
  /**
   * Filtert die Eingabe auf Ziffern.
   *
   * Bekommt das Element selbst, nicht nur den Wert: der Wert im DOM muss unmittelbar
   * korrigiert werden. Über ngModel allein ginge das nicht — tippt jemand einen
   * Buchstaben, bleibt das Modell auf demselben Wert, Angular sieht keine Änderung und
   * schreibt nichts zurück. Der Buchstabe stünde weiter im Feld.
   *
   * Zahlenfelder (type="number") scheiden aus: sie erlauben Vorzeichen und
   * Exponentialschreibweise, verschlucken führende Nullen und zeigen Spinner-Pfeile.
   */
  onCodeInput(element: HTMLInputElement): void {
    const digitsOnly = element.value.replace(/\D/g, '').slice(0, 6);

    this.codeHadInvalidChars = digitsOnly !== element.value;
    element.value = digitsOnly;
    this.confirmationCode = digitsOnly;
  }

  onSubmitCode(): void {
    const code = this.confirmationCode.trim();
    if (this.codeSubmitting) return;

    // Nicht mehr wortlos abbrechen: vorher war der Knopf bei sechs Buchstaben
    // freigegeben und ein Klick blieb folgenlos.
    if (!/^\d{6}$/.test(code)) {
      this.errorMessage = 'Bitte den sechsstelligen Code aus der E-Mail eingeben.';
      this.isLoginFailed = true;
      return;
    }

    this.codeSubmitting = true;
    this.isLoginFailed = false;

    this.authService.confirmEmailCode(this.form.email, code).subscribe({
      next: (data) => {
        this.codeSubmitting = false;
        if (data?.jwt) {
          this.tokenStorage.saveToken(data.jwt);
          this.tokenStorage.saveUser(data);
          this.email = data.email;
          this.isLoggedIn = true;
        } else {
          this.errorMessage = 'Code ungültig oder abgelaufen.';
          this.isLoginFailed = true;
        }
      },
      error: (err) => {
        this.codeSubmitting = false;
        // Feld leeren: der verbrauchte Versuch soll nicht versehentlich wiederholt werden.
        this.confirmationCode = '';
        this.errorMessage = err.error?.message || 'Code ungültig oder abgelaufen.';
        this.isLoginFailed = true;
      }
    });
  }

  /** Zurück zum E-Mail-Formular, um einen neuen Code anzufordern. */
  requestNewCode(): void {
    this.emailSent = false;
    this.confirmationCode = '';
    this.isLoginFailed = false;
    this.errorMessage = '';
  }

  onSubmit(): void {
    if (this.authMode === AuthMode.EMAIL_PASSWORD) {
      // Login mit E-Mail/Benutzername + Passwort
      this.authService.login(this.form).subscribe(
        data => {
          if (data?.jwt) {
            this.tokenStorage.saveToken(data.jwt);
            this.tokenStorage.saveUser(data);
            this.email = data.email;
            this.isLoginFailed = false;
            this.isLoggedIn = true;
          } else {
            this.errorMessage = 'Login fehlgeschlagen.';
            this.isLoginFailed = true;
            setTimeout(() => this.isLoginFailed = false, 3000);
          }
        },
        err => {
          this.errorMessage = err.error?.message || 'Login fehlgeschlagen.';
          this.isLoginFailed = true;
          setTimeout(() => this.isLoginFailed = false, 3000);
        }
      );
    } else if (this.authMode === AuthMode.EMAIL_TOKEN) {
      // Bestätigungs-Mail anfordern
      this.authService.requestEmailLogin(this.form.email, this.es.getActiveLang(), this.eventTitle, this.eventSubtitle, this.uMode).subscribe(
        () => {
          this.emailSent = true;
        },
        err => {
          this.errorMessage = err.error?.message || 'E-Mail konnte nicht gesendet werden.';
          this.isLoginFailed = true;
          setTimeout(() => this.isLoginFailed = false, 3000);
        }
      );
    }
  }

  eLangGlob = computed(() => this.es.getLangGlobals());


}
